"use client";


import Link from 'next/link';
import Logo from '@/components/icons/Logo';
import Button from "@/components/ui/Button";
import { AlignJustify, X } from "lucide-react";
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { AnimatePresence, easeInOut, motion } from "framer-motion";
import { createClient } from "@/utils/supabase/client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Dictionary, Locales } from "@/app/[lang]/dictonaries";
import LanguageSelect from './LanguageSelect';

// The marketing nav (Plans, Contact us, Explore) is gone: every destination it
// offered — meal-plan, gift cards, contact-us, faq, why-calori, process, blog —
// is either retired or now lives on calori.health, and middleware redirects
// those paths off-site. What is left is what still works here: the logo, the
// language switcher, and getting into (or out of) your account.
//
// The dictionary keys for the removed links are deliberately left in
// dictionaries/{en,fi}.json rather than deleted, so restoring a link is a
// markup change only.

interface NavlinksProps {
    onPress: () => void;
    setPressed: (value: boolean) => void;
    pressed: boolean;
    dict: Dictionary,
    lang: Locales
}

export default function Navlinks(props: NavlinksProps) {
    const lang = props.lang;
    const supabase = createClient();
    const [signedIn, setSignedIn] = useState(false);
    const [pressed, setPressed] = useState(props.pressed);
    const [menuAnimationComplete, setMenuAnimationComplete] = useState(false);
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();

    const getInitialSession = async () => {
        try {
            const result = await supabase.auth.getSession();
            if (result.error) throw result.error;

            setSignedIn(!!result.data.session?.user); // Set state based on session status
        } catch (error) {
            console.error("Error retrieving session:", error);
        }
    };

    useEffect(() => {
        props.setPressed(false);
    }, [pathname]);

    useEffect(() => {
        // Get the current session at mount
        getInitialSession();
    }, []);

    useEffect(() => {
        const user = searchParams.get('user');

        if (user) {
            if (user === "logout") {
                setSignedIn(false);
            } else {
                setSignedIn(true);
            }

            const newSearchParams = new URLSearchParams(searchParams.toString());

            newSearchParams.delete('user');

            const redirectPath = `${pathname}?${newSearchParams.toString()}`;
            router.replace(redirectPath, { scroll: false });
        }
    }, [searchParams]);

    supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
            setSignedIn(true);
        }
    });

    function handlePress() {
        props.onPress();
        setPressed(!pressed);
    }

    const handleSignOut = async () => {
        await supabase.auth.signOut();
        setSignedIn(false);
        router.push(`/${lang}`);
    };

    useEffect(() => {
        if (!props.pressed) {
            setMenuAnimationComplete(false);
        }
    }, [props.pressed]);

    // On the meal-plan funnel (only open to gift card holders) a signed-out
    // visitor gets no Sign in button: from sign-in they'd head for sign-up,
    // which middleware redirects to calori.health without the gift card code.
    const hideSignIn =
        !signedIn && /^\/(en|fi)\/meal-plan(\/|$)/.test(pathname ?? '');

    return (
        <div
            className="relative flex w-full top-0 py-4 px-3.5 md:px-1 lg:py-2 align-center h-full justify-center items-center flex-col">
            <div className="flex justify-between lg:justify-start items-center h-full flex-col w-full">
                <div className="hidden lg:flex flex-row w-full justify-between lg:justify-start items-center">
                    <Link href={`/${lang}`} aria-label="Logo">
                        <Logo className="h-[21px] w-[auto]" />
                    </Link>
                    {/* Account */}
                    <div className={"flex flex-row gap-2 lg:ml-auto md:-mr-1"}>
                        <div className='mr-3'>
                            <LanguageSelect locale={lang} />
                        </div>
                        {!hideSignIn && (
                            <Link href={signedIn ? `/${lang}/dashboard` : `/${lang}/signin`} className={"hidden lg:flex"}>
                                <Button className={"text-[#213735] bg-white hover:bg-gray-200"} variant={"slim"}>
                                    {signedIn ? props.dict.components.navbar.cta.dashboard : props.dict.components.navbar.cta.sign_in}
                                </Button>
                            </Link>
                        )}
                        {signedIn && (
                            <Button onClick={handleSignOut} variant={"slim"}
                                className={"bg-transparent hover:bg-white/20 w-full text-white"}>
                                {props.dict.components.navbar.cta.sign_out}
                            </Button>
                        )}
                    </div>
                </div>
                <div className={"flex flex-row w-full justify-between lg:hidden items-center"}>
                    <Link href={`/${lang}`} aria-label="Logo">
                        <Logo className="h-[18px] w-[93px]" />
                    </Link>
                    <button onClick={props.onPress}>
                        {!props.pressed && <AlignJustify />}
                        {props.pressed && <X />}
                    </button>
                </div>
            </div>
            {/* Mobile menu */}
            <div className="w-full">
                <AnimatePresence mode="wait" initial={false}>
                    {props.pressed && <motion.div
                        key={props.pressed ? "mob-nav-open" : "mob-nav-closed"}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: easeInOut }}
                        onAnimationComplete={() => {
                            setMenuAnimationComplete(true);
                        }}
                        className={`lg:hidden flex flex-col w-full mt-5`}>
                        <div>
                            {/* Was spaced to clear the nav links above it; with those
                                gone this is the first row in the menu. */}
                            <div className="w-[100px] mt-1">
                                <LanguageSelect locale={lang} />
                            </div>
                            <div className="flex flex-col gap-4 mt-4 items-stretch">
                                {!hideSignIn && (
                                    <Link href={signedIn ? `/${lang}/dashboard` : `/${lang}/signin/password_signin`}
                                        className={"lg:flex"}>
                                        <Button className={"text-[#213735] bg-white hover:bg-gray-200 w-full"}
                                            variant={"slim"}>
                                            {signedIn ? props.dict.components.navbar.cta.dashboard : props.dict.components.navbar.cta.sign_in}
                                        </Button>
                                    </Link>
                                )}
                                {
                                    signedIn && (
                                        <Button variant={"slim"}
                                            className={cn("text-[#213735] bg-white hover:bg-gray-200", !signedIn && "hidden")}
                                            onClick={handleSignOut}>{props.dict.components.navbar.cta.sign_out}</Button>
                                    )
                                }
                            </div>
                        </div>
                    </motion.div>}
                </AnimatePresence>
            </div>
        </div>
    );
}
